<template>
    <div class="s-menu-tab" :class="className">
        <div v-if="tabs" class="inner-tabs">
            <div class="inner-tabs-title">
                {{ tabs.title }}
            </div>
            <div class="inner-tabs-wrapper">
                <div
                    v-for="(tab, i) in tabs.tabs"
                    :key="`inner-tab-${i}`"
                    class="inner-tabs-item"
                    :class="{ 'inner-tabs-item--active': i === activeTabIndex }"
                    @click="setActiveTabIndex(i)"
                >
                    {{ tab }}
                </div>
            </div>
            <div v-if="card">
                <a-picture :link="card.image" :alt="tabs.title" />
                <div class="content">
                    <div class="title">
                        {{ card.title }}
                    </div>
                    <p class="desc">
                        {{ card.description }}
                    </p>
                    <a-link v-if="card.link" v-bind="card.link" />
                </div>
            </div>
        </div>
        <div class="s-menu-tab-wrapper">
            <div class="s-menu-tab__body" :class="`s-menu-tab__body_type_${id}`">
                <section
                    v-for="(section, index) in sections"
                    :key="`s-menu-section-${index}`"
                    class="s-menu-tab__section"
                    :class="getSectionClassName(section, id)"
                >
                    <template v-if="section.content">
                        <menuTabSection
                            v-for="(item, sectionIndex) in section.content"
                            :id="id"
                            :key="`tab-section-content-${sectionIndex}`"
                            :version="version"
                            :active-tab-index="activeTabIndex"
                            :item="item"
                            :event-location="eventLocation"
                        />
                    </template>
                    <template v-if="section.list">
                        <div
                            v-for="(itemList, itemListIndex) in section.list"
                            :key="`tab-section-list${itemListIndex}`"
                            class="s-menu-tab__section-row"
                        >
                            <div
                                v-for="(itemRow, itemRowIndex) in itemList"
                                :key="`tab-section-list-row-${itemRowIndex}`"
                                class="s-menu-tab__section-column"
                            >
                                <menuTabSection
                                    v-for="(itemCol, itemColIndex) in itemRow"
                                    :id="id"
                                    :key="`tab-section-list-col-${itemColIndex}`"
                                    :version="version"
                                    :active-tab-index="activeTabIndex"
                                    :item="itemCol"
                                />
                            </div>
                        </div>
                    </template>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import menuTabSection from './menu-tab-section.vue';

export default {
    name: 'SMenuTab',

    components: {
        menuTabSection,
        APicture,
        ALink,
    },

    props: {
        /**
         * Menu
         */
        menu: {
            type: Array,
            required: false,
            default: () => [],
        },

        /**
         * Tabs
         */
        tabs: {
            type: Object,
            default: undefined,
        },

        /**
         * Id
         */
        id: {
            type: String,
            default: 'Item',
        },

        /**
         * Active tab id
         */
        activeTab: {
            type: String,
            default: undefined,
        },

        /**
         * If a menu is centered around menu-link (for minisites)
         */
        type: {
            type: String,
            default: undefined,
            validator: (value) => value === undefined || ['mini', 'centered', 'default'].includes(value),
        },

        version: {
            type: String,
            default: '',
        },

        card: {
            type: Object,
            default: undefined,
        },
    },

    emits: ['select-tab', 'select-link', 'onTabChange'],

    data: () => ({
        activeTabIndex: 0,
    }),

    computed: {
        sections() {
            if (Array.isArray(this.menu?.[0])) {
                return this.menu[this.activeTabIndex];
            }
            return this.menu;
        },

        className() {
            const isMini = this.type === 'mini';
            const items = Object.keys(this.menu).length;
            return {
                's-menu-tab_centered': this.type === 'centered',
                's-menu-tab_mini': isMini,
                [`s-menu-tab__items-${items}`]: !isMini,
            };
        },

        eventLocation() {
            return this.tabs?.eventLocation?.[this.activeTabIndex];
        },
    },

    methods: {
        getSectionClassName(section, id) {
            return {
                [`s-menu-tab__section_tabindex_${this.activeTabIndex}`]: this.activeTabIndex !== undefined,
                [`s-menu-tab__section_type_${id}`]: id,
                [`s-menu-tab__section_divider_${section.divider}`]: section.divider,
                's-menu-tab__section_submenu': section.submenu,
                's-menu-tab__section_full-width': section.type === 'full',
            };
        },

        setActiveTabIndex(index) {
            this.activeTabIndex = index;
        },
    },
};
</script>

<style lang="postcss">
.s-menu-tab {
    &.s-menu-tab_centered {
        /* styles for centered version of menu tabs (on minisite) */
        @media (--viewport-desktop) {
            .s-menu-tab {
                &__glyphlist-item-link,
                &__linkmore {
                    vertical-align: top;
                }

                &__body {
                    display: flex;
                    padding: 8px 0;
                    padding-inline-end: 136px;
                    padding-inline-start: 64px;
                    column-gap: 48px;
                }

                &__list,
                &__list ~ .s-menu-tab__linkmore {
                    margin-top: 0;
                }

                &__linkmore,
                &__link-strong {
                    + .s-menu-tab__list,
                    + .s-menu-tab__list ~ .s-menu-tab__linkmore {
                        margin-top: 24px;
                    }
                }

                &__section {
                    &_submenu {
                        padding-inline-start: 24px;

                        &::before {
                            inset-inline-start: 0;
                        }
                    }

                    &_divider {
                        &_short {
                            min-width: 180px;
                        }
                    }
                }
            }
        }
    }

    @media (--viewport-desktop-large) {
        max-width: 1312px;
        margin-inline: auto;
    }

    &__body {
        padding: 0 16px 8px;

        @media (--viewport-tablet) {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            column-gap: 32px;
            padding: 0 48px 8px;
            padding-inline-start: 32px;
        }

        @media (--viewport-desktop) {
            column-gap: 32px;
        }

        @media (--viewport-desktop-wide) {
            column-gap: 72px;
            padding-inline: 32px;
        }

        @media (--viewport-desktop-large) {
            column-gap: 80px;
        }
    }

    &__section {
        padding: 24px 0;

        &_full-width {
            @media (--viewport-tablet) {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        }

        &_divider {
            &_wide,
            &_short {
                position: relative;

                &::before {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    inset-inline-start: 0;
                    height: 1px;
                    background-color: var(--av-brand-accent);

                    @media (--viewport-tablet) {
                        display: none;
                    }
                }
            }

            &_short {
                &::before {
                    width: 54px;
                }
            }

            &_wide {
                &::before {
                    width: 100%;
                }
            }
        }

        &_submenu {
            @media (--viewport-tablet) {
                border-top: 1px solid var(--av-brand-accent);
            }

            @media (--viewport-desktop) {
                position: relative;
                border-top: none;

                &::before {
                    display: block;
                    content: '';
                    position: absolute;
                    top: 0;
                    inset-inline-start: -24px;
                    width: 1px;
                    height: calc(100% + 8px);
                    background-color: var(--av-brand-accent);
                }
            }
        }
    }

    &__section_full-width {
        padding-top: 32px;
        padding-bottom: 40px;
        @media (--viewport-tablet) {
            padding-top: 24px;
            padding-bottom: 36px;
        }
        @media (--viewport-desktop) {
            padding-bottom: 32px;
        }
        @media (--viewport-desktop-wide) {
            padding-bottom: 16px;
        }
        @media (--viewport-desktop-large) {
            padding-bottom: 32px;
        }
    }

    &__linktitle {
        .a-link__content {
            @mixin title-accent;

            @media (--viewport-tablet) {
                @mixin paragraph-accent;
            }
            @mixin title-accent;

            @media (--viewport-tablet) {
                @mixin paragraph-accent;
            }
        }
    }

    &__description {
        display: block;
        margin-top: 8px;
        @mixin descriptor;
        color: var(--av-fixed-secondary);
    }

    &__list {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 16px 0;
    }

    &__text {
        @mixin paragraph;
        color: var(--av-fixed-secondary);

        a:hover {
            color: var(--av-link-primary-hover);
        }
    }

    &__link-strong {
        @mixin body-accent;
        color: var(--av-nav-primary);

        .a-link__content {
            @mixin body-accent;
            color: var(--av-nav-primary);
        }

        &:hover,
        &:focus {
            .a-link__content {
                background: none;
                color: var(--av-nav-primary);
            }
        }

        &.nuxt-link-exact-active {
            .a-link__content {
                color: var(--av-nav-primary);
                pointer-events: auto;
            }
        }
    }

    &__bottom-divider {
        position: relative;
        padding-bottom: 24px;

        &::after {
            content: '';
            display: block;
            position: absolute;
            bottom: 0;
            inset-inline-start: 0;
            width: 130px;
            height: 1px;
            background: var(--av-brand-accent);
        }
    }

    &__list {
        ~ .s-menu-tab {
            &__linkmore {
                margin: 32px 0 0;
            }
        }
    }

    &__list,
    &__cta {
        margin: 24px 0 0;

        &_nomargin {
            margin-top: 0;
        }
        &_inline {
            gap: 16px;
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            .s-menu-tab__cta-item {
                &:not(:first-child) {
                    margin: 0!important;
                }
            }
        }
    }

    &__description + &__list {
        margin-top: 24px !important;
    }

    &__cta-item {
        line-height: 0;

        &:not(:first-child) {
            margin: 16px 0 0;
        }
    }

    &__list-item-link,
    &__cta-item-link {
        @mixin caption-accent;

        &:hover {
            color: var(--av-link-primary-hover);
        }

        @media (--viewport-desktop) {
            @mixin body-accent;
        }
    }

    &__list-item-link {
        @mixin paragraph-accent;

        &.has-star {
            .a-link__content {
                .a-glyph {
                    top: 1px;
                }
            }
        }

        &.text-thin {
            font-weight: 400;
        }

        &--category {
            .a-link__content {
                color: var(--av-nav-primary);
                @mixin paragraph-accent;
            }
        }
    }

    &__cta-item-link {
        padding: 8px;

        &.make-accent {
            background: var(--el-secondary-hover);
            border-radius: 4px;
        }

        .a-link__content:not(:hover) {
            color: var(--av-nav-primary);

            .a-glyph {
                color: var(--av-brand-primary);
            }
        }
    }

    &__glyphlist {
        @media (--viewport-tablet) {
            display: flex;
            align-items: center;
        }
    }

    &__glyphlist-item {
        &:not(:last-of-type) {
            margin-bottom: 24px;

            @media (--viewport-tablet) {
                margin-bottom: 0;
                margin-inline-end: 56px;
            }
        }

        .a-link {
            margin: 0;
        }
    }

    &__glyphlist-item-link {
        .a-glyph {
            margin: 0 0 2px;
            margin-inline-end: 8px;
        }
    }

    &__linkmore,
    &__glyphlist-item-link {
        .a-link__content {
            color: var(--av-nav-primary);

            svg {
                color: var(--av-brand-primary);
            }
        }

        &:hover,
        &:focus {
            .a-link__content {
                background: none;
                color: var(--av-link-primary-hover);

                svg {
                    fill: var(--av-brand-secondary);
                }
            }
        }
    }

    &__linkmore {
        @mixin body-accent;

        .a-link__glyph {
            margin-inline-start: 8px;
        }
    }

    &__glyphlist-item-link {
        @mixin body-accent;
    }

    &__items-4 {
        .s-menu-tab__body {
            display: grid;
            column-gap: 32px;

            .s-menu-tab__section {
                margin: 0;
            }

            @media (--viewport-desktop) {
                grid-template-columns: repeat(4, 1fr);
            }

            @media (--viewport-desktop-wide) {
                column-gap: 24px;
            }
        }

        .s-menu-tab__section {
            @media (--viewport-desktop) {
                max-width: 164px;
            }

            @media (--viewport-desktop-wide) {
                max-width: 264px;
            }

            @media (--viewport-desktop-large) {
                max-width: 288px;
            }
        }

        .s-menu-tab__section:last-child {
            @media (--viewport-tablet) {
                grid-column: span 3;
            }

            @media (--viewport-desktop) {
                grid-column: auto;
            }

            @media (--viewport-desktop-wide) {
                max-width: 204px;
            }

            @media (--viewport-desktop-large) {
                max-width: 260px;
            }
        }
    }

    .s-menu-tab__section-row {
        gap: 32px;
        display: flex;
        flex-direction: column;
        @media (--viewport-desktop) {
            flex-direction: row;
            width: calc(200% + 32px);
        }
        &:not(:first-child) {
            padding-top: 24px;
        }
        .s-menu-tab__section-column {
            width: 100%;
            display: flex;
            flex-direction: column;
            .s-menu-tab__cta {
                padding-top: 16px;
                margin-top: auto!important;
            }
            .s-menu-tab__linktitle {
                margin: 0!important;
                @media (--viewport-desktop) {
                    padding-top: 40px;
                }
            }
            .s-menu-tab__link-strong {
                & ~ .s-menu-tab__linktitle {
                    @media (--viewport-desktop) {
                        padding-top: 8px;
                    }
                }
            }
        }
    }
}

#ot-sdk-btn-floating {
    &.ot-floating-button {
        button {
            text-align: center;

            svg {
                vertical-align: middle;
            }
        }
    }
}

/* old solution tab support */
.s-main-header {
    .s-menu-tab__body_type_solutions {
        @media (--viewport-desktop) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (--viewport-desktop) {
            display: flex;
            gap: 48px;
            justify-content: space-between;
        }
    }
}
</style>

<style lang="postcss" scoped>
.s-menu-tab__list-item {
    .a-link {
        display: inline;
        margin-inline-end: 4px;

        :deep(.a-link__content) {
            display: inline;
        }
    }
}

.s-menu-tab__section_type_company {
    .s-menu-tab__list ~ .s-menu-tab__link-strong {
        margin: 32px 0 0;
    }
}

.s-menu-tab__body_type_company {
    @media only screen and (max-width: 767px) {
        grid-template-columns: repeat(2, 1fr);

        .s-menu-tab__section_divider_short::before {
            content: none;
        }
    }
}

.inner-tabs {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px 16px 24px;
    background: var(--av-inversed-primary);

    @media (--viewport-tablet) {
        flex-direction: row;
        align-items: center;
        gap: 30px;
        padding: 0 32px 24px 32px;
    }

    @media (--viewport-desktop) {
        padding: 32px;
        flex-direction: column;
        align-items: flex-start;
        flex-shrink: 0;
        gap: 0;
        background: var(--av-fixed-info-accent);
    }

    @media (--viewport-desktop-wide) {
        padding: 32px 80px 32px 64px;
    }

    @media (--viewport-desktop-large) {
        padding: 32px 100px 32px 64px;
    }

    &-title {
        display: none;

        @media (--viewport-desktop) {
            @mixin title-accent;
            color: var(--av-nav-primary);
            display: block;
            padding: 8px 0 8px 0;
            margin-bottom: 16px;
        }
    }

    &-wrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;

        @media (--viewport-tablet) {
            flex-direction: row;
        }

        @media (--viewport-desktop) {
            flex-direction: column;
        }
    }

    &-item {
        @mixin lead-accent;
        color: var(--av-brand-primary);
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;

        &--active {
            color: var(--av-nav-primary);
            background: var(--av-fixed-info-light);
            pointer-events: none;
        }
    }
}

.s-menu-tab-wrapper {
    width: 100%;
}
</style>
